export enum DocumentFieldType {
    Text = 'text',
}

export enum DocumentCitationStyle {
    APA7 = 'apa-7',
    ISO690_2011_Harvard = 'iso690-2011-harvard',
    ISO690_2011_Vancouver = 'iso690-2011-vancouver',
    ISO690_2011_Footnote = 'iso690-2011-footnote',
    ISO690_2022_Harvard = 'iso690-2022-harvard',
    ISO690_2022_Vancouver = 'iso690-2022-vancouver',
    ISO690_2022_Footnote = 'iso690-2022-footnote',
}

export enum DocumentCaptionsPosition {
    Above = 'above',
    Below = 'below',
}

export enum DocumentSectionType {
    ToC = 'toc',
    Content = 'content',
    CitationList = 'citationList',
    ShortcutList = 'shortcutList',
    FigureList = 'figureList',
    AttachmentList = 'attachmentList',
    Attachments = 'attachments',
    OtherAttachments = 'otherAttachments',
}

export type AttachmentNumberingType = 'arabic' | 'roman' | 'alphabetic';
export type AttachmentNumberPosition = 'before' | 'after';

export interface AttachmentNumberingConfig {
    label: string;
    numberingType: AttachmentNumberingType;
    numberPosition: AttachmentNumberPosition;
    trailingDot?: boolean;
}

export enum DocumentLanguage {
    CZ = 'cz',
    SK = 'sk',
}

export type SlateContents = {
    children: unknown[];
};

export interface DocumentField {
    id: string;
    type: DocumentFieldType;
    label: string;
    placeholder?: string;
    size?: 'md'|'lg';
    isRequired: boolean;
}

export interface DocumentContentField {
    id: string;
    isRequired: boolean;
    displayWordCount?: boolean;
}

export interface DocumentSection_Base {
    id: string;
    title: string;
    instructions?: string;
    countCharacters?: boolean;
    noPageBreak?: boolean;
}

export interface DocumentSection_ToC extends DocumentSection_Base {
    type: DocumentSectionType.ToC;
}

export interface DocumentSection_Content extends DocumentSection_Base {
    type: DocumentSectionType.Content;
    fields: DocumentField[];
    content: DocumentContentField|null;
}

export interface CitationGroup {
    id: string;
    label: string;
    subgroups?: CitationGroup[];
}

export interface DocumentSection_CitationList extends DocumentSection_Base {
    type: DocumentSectionType.CitationList;
    citationGroups?: CitationGroup[];
}

export type ShortcutListSeparator = 'dash' | 'tab';

export interface DocumentSection_ShortcutList extends DocumentSection_Base {
    type: DocumentSectionType.ShortcutList;
    shortcutListSeparator?: ShortcutListSeparator;
}

export type FigureListKind = 'all' | 'images' | 'tables' | 'charts' | 'maps' | 'formulas' | 'other';

export interface DocumentSection_FigureList extends DocumentSection_Base {
    type: DocumentSectionType.FigureList;
    figureListKind?: FigureListKind;
    figureMinCount?: number;
}

export interface DocumentSection_AttachmentList extends DocumentSection_Base {
    type: DocumentSectionType.AttachmentList;
    tocShowPageNumbers?: boolean;
}

export type AttachmentNumberingMode = 'default' | 'custom';

export interface DocumentSection_Attachments extends DocumentSection_Base {
    type: DocumentSectionType.Attachments;
    contentId: string;
    attachmentNumberingMode?: AttachmentNumberingMode;
    attachmentLabel?: string;
    attachmentNumberingType?: AttachmentNumberingType;
    attachmentNumberPosition?: AttachmentNumberPosition;
    attachmentTrailingDot?: boolean;
}

export interface DocumentSection_OtherAttachments extends DocumentSection_Base {
    type: DocumentSectionType.OtherAttachments;
    contentId: string;
}

export type DocumentSection =
    | DocumentSection_ToC
    | DocumentSection_Content
    | DocumentSection_CitationList
    | DocumentSection_ShortcutList
    | DocumentSection_FigureList
    | DocumentSection_AttachmentList
    | DocumentSection_Attachments
    | DocumentSection_OtherAttachments;

export interface DocumentTypeOptions {
    label: string;
    variables: Record<string, string>;
    aiPrompt: string;
    minPages: number;
    maxPages: number|null;
}

export interface DocumentConfigBase {
    citationStyle: DocumentCitationStyle;
    captionsPosition: DocumentCaptionsPosition;
    captionsPositionForImages: DocumentCaptionsPosition;
    captionsPositionForTables: DocumentCaptionsPosition;
    captionsPositionForCharts: DocumentCaptionsPosition;
    language: DocumentLanguage;
    figurePrefixes: {
        image: string;
        table: string;
        chart: string;
        formula: string;
        map: string;
    },
    citationPageNumberPrefix: string;
    attachmentNumberFormat?: string;
    captionChapterNumbers?: boolean;
    citationCustomInstructions?: string;
}

export interface DocumentConfig extends DocumentConfigBase {
    types: Record<string, DocumentTypeOptions>;
    sections: DocumentSection[];
}

export interface DocumentTemplate {
    templateId: string;
    label: string;
    iconPath: string;
    docxPath: string;
    config: DocumentConfig;
}

export interface DocumentFieldState {
    value: unknown;
}

export interface DocumentCitationState {
    userLabel: string;
    source: string;
    citation?: string|null;
    content?: SlateContents;
    groupId?: string;
}

export interface DocumentShortcutState {
    shortcut: string;
    meaning?: string|null;
    content?: SlateContents;
}

export interface DocumentData {
    templatePath: string;
    fields: Record<string, unknown>;
    contents: Record<string, SlateContents>;
    citations: Record<string, DocumentCitationState>;
    shortcuts: Record<string, DocumentShortcutState>;
    footnotesOffset?: Record<string, number>;
    characterCountPerSection?: Record<string, number>;
}

export interface DocumentExportRequestData extends DocumentConfigBase, DocumentData {
    citationGroups?: CitationGroup[];
    attachmentNumberingConfig?: AttachmentNumberingConfig;
    figureMinCounts?: Record<string, number>;
    attachmentListTocShowPageNumbers?: boolean;
    shortcutListSeparator?: ShortcutListSeparator;
    noPageBreakSections?: string[];
}
